/**
 * SectionHeader — title-row for a Section.
 *
 * Three observed compositions in the product, all covered by the same
 * primitive via optional slots:
 *
 *   1. Title only — "KPI и показатели", "Кампании".
 *   2. Title + inline badges — "ИИ-модуль [Бета] [Не запущен]".
 *   3. Title + right-side actions — "Все сенсоры             [Отчёт]".
 *
 * Combinations are allowed: title + badges + actions all at once.
 *
 * @format
 */

import {forwardRef, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {text} from 'ui/theme/typography';
import {Row} from 'ui/components/layout/Row';

export interface SectionHeaderProps {
  title?: ReactNode;
  badges?: ReactNode;
  actions?: ReactNode;
  className?: string;
}

const Title = styled('h2')(({theme}) => ({
  ...text('heading.m'),
  color: theme.palette.surface.onSurface,
  margin: 0,
  minWidth: 0,
}));

const BadgesSlot = styled(Row)({
  flex: '0 0 auto',
});

const ActionsSlot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: '0 0 auto',
});

export const SectionHeader = forwardRef<HTMLDivElement, SectionHeaderProps>(function SectionHeader(
  {title, badges, actions, className},
  ref,
) {
  return (
    <Row ref={ref} gap='s' alignY='center' justify='between' reflowAt='sm' className={className}>
      <Row gap='s' alignY='center'>
        {title != null && <Title>{title}</Title>}
        {badges != null && (
          <BadgesSlot gap='xs' alignY='center'>
            {badges}
          </BadgesSlot>
        )}
      </Row>
      {actions != null && <ActionsSlot>{actions}</ActionsSlot>}
    </Row>
  );
});
